import React, { useCallback, useEffect, useState } from "react";
import { API_BASE } from "./utils/api";

// Helper fetch với credentials
async function fetchWithAuth(url, options = {}) {
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
}

function DriverRevenue({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const vehicleNumber = user?.drive_number;

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Encode một lần (Express sẽ tự động decode)
      const encodedVehicleNumber = encodeURIComponent(vehicleNumber);
      const res = await fetchWithAuth(
        `${API_BASE}/revenue/driver/${encodedVehicleNumber}`
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        if (res.status === 403) {
          throw new Error(
            errorData.message || "Bạn không có quyền xem dữ liệu này."
          );
        } else if (res.status === 404) {
          throw new Error(errorData.message || "Không tìm thấy dữ liệu.");
        } else if (res.status === 400) {
          throw new Error(errorData.message || "Dữ liệu không hợp lệ.");
        }
        throw new Error(errorData.message || "Lỗi khi tải dữ liệu.");
      }

      const result = await res.json();
      if (!result.success) {
        throw new Error(result.message || "Không thể tải dữ liệu");
      }
      setData(result.data);
    } catch (err) {
      console.error("Lỗi khi load data:", err);
      setError(err.message || "Có lỗi xảy ra");
    } finally {
      setLoading(false);
    }
  }, [vehicleNumber]);

  useEffect(() => {
    if (!vehicleNumber) {
      setError("Bạn chưa được gán số xe. Vui lòng liên hệ admin.");
      setLoading(false);
      return;
    }

    // Validate vehicle number format on client side
    if (vehicleNumber.length > 20 || /[<>'"&;\\/]/.test(vehicleNumber)) {
      setError("Số xe không hợp lệ.");
      setLoading(false);
      return;
    }

    loadData();
  }, [vehicleNumber, user, loadData]);

  const handleLogout = async () => {
    try {
      await fetchWithAuth(`${API_BASE}/auth/logout`, {
        method: "POST",
      });
      window.location.reload();
    } catch (err) {
      console.error("Lỗi khi đăng xuất:", err);
    }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-600 text-sm">Đang tải dữ liệu...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-lg border border-red-200 p-8 max-w-md w-full">
          <h2 className="text-xl font-semibold text-red-900 mb-4">Lỗi</h2>
          <p className="text-red-700 mb-6">{error}</p>
          <button
            onClick={handleLogout}
            className="w-full inline-flex items-center justify-center rounded-md bg-slate-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-700"
          >
            Đăng xuất
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Không có dữ liệu</p>
      </div>
    );
  }

  const { rows, summary } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-900 mb-1 sm:mb-2 break-words">
                Doanh thu & Tổng nợ - Xe {vehicleNumber}
              </h1>
              <p className="text-xs sm:text-sm text-slate-600">
                Xin chào, <span className="font-semibold">{user?.name}</span>
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-md border border-slate-200 px-4 py-2 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Đăng xuất
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-slate-500 mb-1">
              Tổng doanh thu
            </p>
            <p className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-900 break-words">
              {summary.totalDoanhThu.toLocaleString("vi-VN")} đ
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-slate-500 mb-1">Đã chuyển</p>
            <p className="text-lg sm:text-xl md:text-2xl font-semibold text-emerald-700 break-words">
              {summary.totalDaChuyen.toLocaleString("vi-VN")} đ
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 sm:p-4 col-span-2 md:col-span-1">
            <p className="text-xs sm:text-sm text-slate-500 mb-1">Còn nợ</p>
            <p
              className={`text-lg sm:text-xl md:text-2xl font-semibold break-words ${
                summary.totalNo >= 2000000
                  ? "text-red-700"
                  : summary.totalNo > 0
                  ? "text-amber-700"
                  : "text-emerald-700"
              }`}
            >
              {summary.totalNo.toLocaleString("vi-VN")} đ
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <h2 className="text-sm sm:text-base font-semibold text-slate-900">
              Chi tiết doanh thu
            </h2>
            <button
              onClick={loadData}
              disabled={loading}
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              {loading ? "Đang tải..." : "Làm mới"}
            </button>
          </div>
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <table className="min-w-full divide-y divide-slate-200 text-xs sm:text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                      Ngày
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                      Lái xe
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                      Điều phối
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap">
                      Doanh thu
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap">
                      Đã chuyển
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-center text-xs font-semibold text-slate-600 whitespace-nowrap">
                      Thiếu/Thừa
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                      Ghi chú
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-2 sm:px-3 py-6 text-center text-xs text-slate-500"
                      >
                        Chưa có dữ liệu.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const conNo =
                        (row.doanhThu || 0) - (row.keToanChuyen || 0);
                      const hasDoanhThu = row.doanhThu && row.doanhThu > 0;
                      const hasKeToan = row.keToanChuyen !== '' && row.keToanChuyen !== null && row.keToanChuyen !== undefined;
                      const needsAttention = hasDoanhThu && !hasKeToan;
                      const dieuPhoiChuaNhap = !hasDoanhThu && hasKeToan;
                      return (
                        <tr key={row.rowIndex} className={`${needsAttention ? 'bg-amber-50' : 'bg-white'} hover:bg-slate-100`}>
                          <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-slate-700">
                            {row.ngay ? row.ngay.replace(/[<>]/g, "") : "—"}
                          </td>
                          <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-slate-700">
                            {row.laiXe ? row.laiXe.replace(/[<>]/g, "") : "—"}
                          </td>
                          <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-slate-700">
                            {row.dieuPhoi
                              ? row.dieuPhoi.replace(/[<>]/g, "")
                              : "—"}
                          </td>
                          <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-right text-slate-700">
                            {row.doanhThu
                              ? row.doanhThu.toLocaleString("vi-VN") + " đ"
                              : "—"}
                          </td>
                          <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-right text-slate-700">
                            {row.keToanChuyen !== "" &&
                            row.keToanChuyen !== null &&
                            row.keToanChuyen !== undefined
                              ? row.keToanChuyen.toLocaleString("vi-VN") + " đ"
                              : "—"}
                          </td>
                          <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-center">
                            <span
                              className={
                                row.doiChieuNote
                                  ?.toLowerCase()
                                  .includes("ok")
                                  ? "text-emerald-700 font-semibold"
                                  : row.doiChieuNote
                                      ?.toLowerCase()
                                      .includes("trước")
                                  ? "text-blue-600 font-semibold"
                                  : row.doiChieuNote
                                      ?.toLowerCase()
                                      .includes("thiếu")
                                  ? "text-rose-700 font-semibold"
                                  : row.doiChieuNote
                                      ?.toLowerCase()
                                      .includes("thừa")
                                  ? "text-amber-700 font-semibold"
                                  : dieuPhoiChuaNhap
                                  ? "text-blue-600 font-semibold"
                                  : conNo > 0
                                  ? "text-amber-700"
                                  : conNo < 0
                                  ? "text-blue-700"
                                  : "text-emerald-700"
                              }
                              title={row.doiChieuNote || ""}
                            >
                              {row.doiChieuNote ||
                                (dieuPhoiChuaNhap
                                  ? `Trước ${(row.keToanChuyen || 0).toLocaleString(
                                      "vi-VN"
                                    )} đ`
                                  : conNo !== 0
                                  ? conNo > 0
                                    ? `Thiếu ${Math.abs(conNo).toLocaleString(
                                        "vi-VN"
                                      )} đ`
                                    : `Thừa ${Math.abs(conNo).toLocaleString(
                                        "vi-VN"
                                      )} đ`
                                  : "Khớp")}
                            </span>
                          </td>
                          <td
                            className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-slate-700 max-w-[150px] truncate"
                            title={row.noteNhap || ""}
                          >
                            {row.noteNhap
                              ? row.noteNhap.replace(/[<>]/g, "")
                              : "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DriverRevenue;

